import React, { useEffect, useState } from 'react';
import { X, ExternalLink, Download, FileText, Receipt as ReceiptIcon } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  invoice:       { label: 'Invoice',        icon: FileText,    goto: (r) => `/admin/invoices/${r.ref_id}` },
  quote_payment: { label: 'Quote payment',  icon: FileText,    goto: (r) => `/admin/quotes/${r.ref_id}` },
  receipt:       { label: 'Receipt',        icon: ReceiptIcon, goto: (r) => `/admin/receipts/${r.ref_id}` },
};

/**
 * Drill-down modal showing every payment that landed in a given pay cycle.
 * Opened from the dashboard's "Current Pay Cycle" widget so admins can
 * immediately see which payments make up the number.
 */
export const PayCycleDetailModal = ({ cycleKey, cycleLabel, onClose, navigate }) => {
  const password = sessionStorage.getItem('admin_password');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cycleKey) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/analytics/pay-cycles/${cycleKey}/rows`, {
          headers: { Authorization: `Basic ${btoa(':' + password)}` },
        });
        if (!res.ok) throw new Error('load failed');
        const d = await res.json();
        if (!cancelled) setData(d);
      } catch (err) {
        logger.error('Pay-cycle rows:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cycleKey, password]);

  const goto = (row) => {
    const meta = TYPE_META[row.type];
    if (!meta || !row.ref_id) return;
    onClose();
    navigate(meta.goto(row));
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="pay-cycle-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground">Payments in this cycle</h2>
            <p className="text-sm text-foreground/60">{cycleLabel || data?.label || cycleKey}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Close"
            data-testid="pay-cycle-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-foreground/60">Loading…</div>
          ) : !data ? (
            <div className="p-10 text-center text-red-600">Failed to load</div>
          ) : data.rows.length === 0 ? (
            <div className="p-10 text-center text-foreground/50">
              No payments landed in this cycle yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-foreground/60 sticky top-0">
                <tr>
                  <th className="text-left px-5 py-2">Date</th>
                  <th className="text-left px-5 py-2">Type</th>
                  <th className="text-left px-5 py-2">Reference</th>
                  <th className="text-left px-5 py-2">Client</th>
                  <th className="text-right px-5 py-2">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.rows.map((r, idx) => {
                  const meta = TYPE_META[r.type];
                  const Icon = meta ? meta.icon : FileText;
                  const clickable = !!(meta && r.ref_id);
                  return (
                    <tr
                      key={`${r.type}-${r.ref_id || idx}`}
                      onClick={clickable ? () => goto(r) : undefined}
                      data-testid={`pay-cycle-row-${r.type}-${r.ref_id || idx}`}
                      className={clickable ? 'hover:bg-gray-50 cursor-pointer' : ''}
                    >
                      <td className="px-5 py-3 text-foreground/70 font-mono text-xs">{r.date}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs text-foreground/70">
                          <Icon className="w-3.5 h-3.5" />
                          {meta ? meta.label : r.type}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-foreground/80">{r.ref || '—'}</td>
                      <td className="px-5 py-3 truncate max-w-[220px]">{r.client || '—'}</td>
                      <td className="px-5 py-3 text-right font-semibold">
                        {r.currency || 'CI$'}{Number(r.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {data.rows.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 font-bold">
                    <td className="px-5 py-3" colSpan={4}>Total · {data.count} payment{data.count === 1 ? '' : 's'}</td>
                    <td className="px-5 py-3 text-right">CI${Number(data.total).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-border flex justify-between items-center">
          <a
            href={`${API_URL}/api/admin/analytics/pay-cycles/${cycleKey}/export.csv`}
            className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-1.5"
            onClick={async (e) => {
              // Basic auth doesn't attach to plain links — fetch and pass via download blob.
              e.preventDefault();
              try {
                const res = await fetch(e.currentTarget.href, {
                  headers: { Authorization: `Basic ${btoa(':' + password)}` },
                });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `pay-cycle-${cycleKey}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) { logger.error('CSV download:', err); }
            }}
            data-testid="pay-cycle-modal-csv"
          >
            <Download className="w-4 h-4" />
            Download CSV
          </a>
          <button
            onClick={() => { onClose(); navigate('/admin/pay-cycles'); }}
            className="text-sm text-primary hover:underline inline-flex items-center gap-1.5"
            data-testid="pay-cycle-modal-full-analytics"
          >
            Full pay-cycles analytics
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayCycleDetailModal;
